import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AxiosError } from 'axios'
import { useDispatch } from 'react-redux'
import { trackInternalEvent } from '@rio/tracking'

import { useI18n } from 'builder/hooks/useI18n'
import { actions } from 'builder/modules/jobTracking'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { useAutoApply } from 'builder/views/AutoApply/hooks/useAutoApply'
import {
  useGetRecommendationJob,
  useInvalidateGetRecommendationJobQuery,
} from 'builder/components/FindJob/JobAlertPage/hooks/useGetRecommendationJob'

import { Job } from 'builder/components/FindJob/JobSearchResult/types'
import { getRecommendationsError } from 'builder/components/FindJob/utils/getRecommendationsError'
import { mockJobRecommendations } from 'builder/components/FindJob/RecommandationsSlider/mockJobRecommendations'
import { withBlurredErrorContainer } from 'builder/components/FindJob/RecommendationsErrors/hoc/withBlurredErrorContainer'

import Loader from 'builder/components/CareerPathJobDetails/components/JobDetails/components/Loader'
import { SetTargetLocationDialog } from 'builder/components/FindJob/RecommendationsMissingInfoComponents'
import { NoResultsRecommendation } from 'builder/components/FindJob/Banners/NoResultsSearch/NoResultsRecommendation'

import { useCareerProfileData } from 'builder/hooks/useCareerProfileData'
import { useViewedJobs } from './useViewedJobs'
import { Card } from './Card/Card'
import { NoLocation } from './NoLocation/NoLocation'
import * as Styles from './styles'

const TRANSLATIONS = 'builder.dashboard.recommended_jobs_widget'

const RecommendedJobs = () => {
  const [displaySetLocationDialog, setDisplaySetLocationDialog] = useState<boolean>(false)

  const { i18n } = useI18n()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { viewedJobs, addViewedJob } = useViewedJobs()
  const { isTablet, isPhone } = useMediaQueries()

  const { invalidate } = useInvalidateGetRecommendationJobQuery()
  const { careerProfileData: careerProfile } = useCareerProfileData()
  const { isUserOnAutoApplyPlan, hasUserCompletedQuestionnaire } = useAutoApply()
  const recommendationsResponse = useGetRecommendationJob(
    true,
    isUserOnAutoApplyPlan && !!hasUserCompletedQuestionnaire,
  )

  const navigateToJobSearch = () => {
    navigate('/job-search?view=search')
  }

  const afterProfileUpdateHandler = () => {
    invalidate()
  }

  const navigateToJobRecommendation = () => {
    trackInternalEvent('recommended_jobs_click', {
      label: 'see_all',
      term: careerProfile?.targetRoles.map(el => el.standardTitle).join(';'),
      location: careerProfile?.targetLocations.map(el => el.formattedName).join(';'),
    })
    navigate('/job-search?view=recommendation')
  }

  const handleCardClick = (card: Job) => {
    trackInternalEvent('recommended_jobs_click', {
      label: 'job_post_click',
      term: careerProfile?.targetRoles.map(el => el.standardTitle).join(';'),
      location: careerProfile?.targetLocations.map(el => el.formattedName).join(';'),
    })
    addViewedJob(card.external_slug_id)
    navigate(`/job-search?view=recommendation&external_slug_id=${card.external_slug_id}`)
  }

  const handleProfileClick = () => {
    trackInternalEvent('click_profile', {
      label: 'recommended_jobs_dashboard',
    })
  }

  const error = useMemo(() => {
    if (!recommendationsResponse.isError || !recommendationsResponse.error) return null
    const error = recommendationsResponse.error as AxiosError<{ missing_attributes: string[] }>
    const missingAttributes = error.response?.data?.missing_attributes || []
    return getRecommendationsError(missingAttributes)
  }, [recommendationsResponse.isError, recommendationsResponse.error])

  const ErrorComponent = useMemo(() => withBlurredErrorContainer(error), [error])

  const getMockedJobs = useMemo(() => {
    return [
      ...mockJobRecommendations,
      ...(isTablet || isPhone ? [] : mockJobRecommendations.slice(0, 2)),
    ]
  }, [isTablet, isPhone])

  const cards = useMemo(() => {
    if (recommendationsResponse.error) {
      return error ? getMockedJobs : []
    }
    return recommendationsResponse.data?.jobs.slice(0, isTablet ? 5 : isPhone ? 4 : 8) || []
  }, [
    recommendationsResponse.error,
    recommendationsResponse.data?.jobs,
    isTablet,
    isPhone,
    error,
    getMockedJobs,
  ])

  const isProfileInfoMissing = useMemo(() => {
    return (
      !careerProfile?.targetLocations?.length &&
      !recommendationsResponse.data?.stats.location_from_resume
    )
  }, [careerProfile, recommendationsResponse.data?.stats])

  useEffect(() => {
    dispatch(actions.fetchJobsRequest())
  }, [dispatch])

  useEffect(() => {
    if (
      !recommendationsResponse.isLoading &&
      recommendationsResponse.data &&
      !recommendationsResponse.isError &&
      careerProfile
    ) {
      trackInternalEvent('job_results', {
        label: 'dashboard',
        term: careerProfile?.targetRoles.map(el => el.standardTitle).join(';'),
        location: careerProfile?.targetLocations.map(el => el.formattedName).join(';'),
        location_from_resume: recommendationsResponse.data.stats.location_from_resume,
        target_role_from_resume: recommendationsResponse.data.stats.target_role_from_resume,
        total_jobs: recommendationsResponse.data.stats.total_jobs,
      })
    }
  }, [
    recommendationsResponse.isLoading,
    recommendationsResponse.isError,
    recommendationsResponse.data,
    careerProfile,
  ])

  return (
    <>
      <Styles.Container id="dashboard-recommended-jobs" data-testid="recommended-jobs-container">
        <Styles.HeaderContainer>
          <Styles.Header data-testid="recommended-jobs-header">
            {i18n.t(`${TRANSLATIONS}.header`)}
          </Styles.Header>
          <Styles.Info data-testid="recommended-jobs-info">
            {i18n.t(`${TRANSLATIONS}.info`)}{' '}
            <Styles.ProfileLink
              data-testid="profile-link"
              to="/career-profile/profile"
              onClick={handleProfileClick}
            >
              {i18n.t(`${TRANSLATIONS}.profile`)}
            </Styles.ProfileLink>
          </Styles.Info>
        </Styles.HeaderContainer>
        <Styles.ContentWrapper>
          {recommendationsResponse.isLoading && <Loader data-testid="loader" />}
          {!recommendationsResponse.isLoading && !!cards?.length && (
            <Styles.CardsList>
              {cards.map((card: Job, index: number) => {
                if (
                  index === 0 &&
                  !!recommendationsResponse?.data?.jobs?.length &&
                  (isProfileInfoMissing || !!error)
                ) {
                  return (
                    <NoLocation key={index} onClick={() => setDisplaySetLocationDialog(true)} />
                  )
                }
                return (
                  <Card
                    key={index}
                    card={card}
                    viewedJobs={viewedJobs}
                    handleClick={() => handleCardClick(card)}
                  />
                )
              })}
            </Styles.CardsList>
          )}
          {recommendationsResponse.isError && !!error && (
            <ErrorComponent
              data-testid="recommendations-error"
              analyticEventsLabel="recommended_jobs_dashboard"
            />
          )}
          {!recommendationsResponse.isLoading && !cards?.length && !error && (
            <NoResultsRecommendation
              CustomContainer={Styles.NoResultContainer}
              handleSearch={navigateToJobSearch}
              showExtension={false}
            />
          )}
        </Styles.ContentWrapper>
        {!recommendationsResponse.isLoading && !!cards?.length && !error && (
          <Styles.ActionContainer>
            <Styles.ActionButton
              data-testid="see-all-recommendations-button"
              onClick={navigateToJobRecommendation}
            >
              {i18n.t(`${TRANSLATIONS}.button`)}
            </Styles.ActionButton>
          </Styles.ActionContainer>
        )}
      </Styles.Container>
      {displaySetLocationDialog && (
        <SetTargetLocationDialog
          afterSetLocationHandler={afterProfileUpdateHandler}
          closeHandler={() => setDisplaySetLocationDialog(false)}
        />
      )}
    </>
  )
}

export default React.memo(RecommendedJobs)
